import { useTranslation } from 'react-i18next';
import Modal from '../Modal/Modal';
import './EasybillTokenModal.scss';
import EasybillIcon from '../../icons/easybill.svg';
import Input from '../Input/Input';
import { FormikErrors, useFormik } from 'formik';
import Button from '../Button';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { getEasybillTokenStatus, postEasybillToken } from '../../core/api';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useQueryClient } from 'react-query';
import { addNotification } from '../../core/store/notificationsSlice';
import { DEFAULT_ERROR } from '../Notifications/Notification';
import { AxiosError } from 'axios';
import MiniLoader from '../MiniLoader/MiniLoader';

interface Props {
  isModalOpened: boolean;
  closeModal: () => void;
  initialIsTokenValid: boolean;
}

interface FormValues {
  token: string;
}

export default function EasybillTokenModal({
  isModalOpened,
  closeModal,
  initialIsTokenValid,
}: Props) {
  const [isLoading, setIsLoading] = useState(initialIsTokenValid);
  const [isTokenValid, setIsTokenValid] = useState(initialIsTokenValid);
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.user.user);
  const activeCompany = useAppSelector((s) => s.user.activeCompany);
  const queryClient = useQueryClient();

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      token: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.token) {
        errors.token = t('Required');
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setIsTokenValid(true);
        await postEasybillToken(values.token, activeCompany?.company_id);

        dispatch(
          addNotification({
            type: 'success',
            text: t('Easybill connected'),
          })
        );

        try {
          const res = await getEasybillTokenStatus(activeCompany?.company_id);
          queryClient.setQueryData(
            ['SellerEasybill', user!.Id, activeCompany?.company_id],
            res
          );
          closeModal();
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          dispatch(
            addNotification({
              text: DEFAULT_ERROR,
              type: 'error',
            })
          );
        }
      } catch (err) {
        setIsLoading(false);

        if ((err as AxiosError).response?.status === 400) {
          setIsTokenValid(false);
        } else {
          setIsLoading(false);
          dispatch(
            addNotification({
              text: DEFAULT_ERROR,
              type: 'error',
            })
          );
        }
      }
    },
  });

  useEffect(() => {
    if (isModalOpened) {
      formik.resetForm();
      setIsTokenValid(initialIsTokenValid);
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [isModalOpened]);

  return (
    <Modal
      isOpened={isModalOpened}
      close={closeModal}
      title='easybill token'
      name='easybill'
      isLoading={isLoading}
    >
      <div className='mx-3 pt-3 pb-4 px-3'>
        <div
          className='d-flex align-items-center fw-800 Text--20 mb-3'
          data-testid='integrations-easybill'
        >
          <img
            src={EasybillIcon}
            alt='easybill'
            style={{
              width: 40,
              height: 40,
            }}
            className='flex-shrink-0 me-2'
          />
          <span>easybill</span>
        </div>
        <form onSubmit={formik.handleSubmit}>
          {!isTokenValid && (
            <div
              className='EasybillTokenModal__badge p-2 Text--red text-center mb-3'
              data-testid='EasybillTokenModal-badge'
            >
              {t('Invalid Easybill token')}
            </div>
          )}
          <Input
            name='token'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.token}
            error={
              !formik.errors.token
                ? isTokenValid
                  ? ''
                  : 'error'
                : formik.errors.token
            }
            touched={!isTokenValid || formik.touched.token}
            className={classNames('Input--EasybillTokenModal', {
              'Input--EasybillTokenModal--hiddenError':
                !isTokenValid && !formik.errors.token,
            })}
          />
          <div className='row row-cols-auto gx-2'>
            <div className='col'>
              <Button
                type='submit'
                disabled={isLoading}
                className='d-flex align-items-center'
                data-testid='EasybillTokenModal-submit-btn'
              >
                {isLoading && (
                  <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center me-2 MiniLoader--gray' />
                )}
                <span>{t('Connect')}</span>
              </Button>
            </div>
            <div className='col'>
              <Button
                styling='outline'
                onClick={closeModal}
                type='button'
                data-testid='EasybillTokenModal-cancel-btn'
              >
                {t('Cancel')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
